import React from 'react';
import styled, { keyframes } from 'styled-components';
import { ReactTyped } from 'react-typed';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { Link } from 'react-scroll';
import { FaArrowRight, FaGithub, FaLinkedin, FaMapMarkerAlt } from 'react-icons/fa';
import { FiDownload } from 'react-icons/fi';
import { HiArrowDown } from 'react-icons/hi';
import Magnetic from './ui/Magnetic';
import SplitText from './ui/SplitText';
import TechOrbit from './ui/TechOrbit';
import { getTechIcon } from './ui/techIcon';
import { useI18n } from '../context/LanguageContext';

const drift = keyframes`
  0% { transform: translate(0,0) scale(1); }
  50% { transform: translate(4%,-3%) scale(1.08); }
  100% { transform: translate(0,0) scale(1); }
`;
const driftAlt = keyframes`
  0% { transform: translate(0,0) scale(1); }
  50% { transform: translate(-5%,4%) scale(1.12); }
  100% { transform: translate(0,0) scale(1); }
`;
const bob = keyframes`
  0%,100% { transform: translateY(0); opacity: 0.6; }
  50% { transform: translateY(8px); opacity: 1; }
`;

const Wrap = styled.section`
  position: relative;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  overflow: hidden;
  width: 100%;
  padding: 7rem 0 4rem;

  @media (max-width: 768px) {
    padding: 6rem 0 3rem;
  }
`;

const Container = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: var(--max-width);
  margin: 0 auto;
  padding: 0 2rem;

  @media (max-width: 768px) {
    text-align: center;
    padding: 0 1.25rem;
  }
`;

const ParallaxLayer = styled(motion.div)`
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
`;

const Mesh = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  span {
    position: absolute;
    border-radius: 50%;
    filter: blur(80px);
    opacity: var(--mesh-op);
  }
  span:nth-child(1){ width:clamp(420px,34vw,760px);aspect-ratio:1;height:auto;top:-8%;left:-4%;
    background:radial-gradient(circle,#6366f1,transparent 70%); animation:${drift} 14s ease-in-out infinite; }
  span:nth-child(2){ width:clamp(380px,30vw,680px);aspect-ratio:1;height:auto;top:18%;right:-4%;
    background:radial-gradient(circle,#ec4899,transparent 70%); animation:${driftAlt} 18s ease-in-out infinite; }
  span:nth-child(3){ width:clamp(320px,26vw,600px);aspect-ratio:1;height:auto;bottom:-10%;left:34%;
    background:radial-gradient(circle,#a855f7,transparent 70%); animation:${drift} 20s ease-in-out infinite; }
`;

const Grid = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
  background-image:
    linear-gradient(var(--border) 1px, transparent 1px),
    linear-gradient(90deg, var(--border) 1px, transparent 1px);
  background-size: 56px 56px;
  opacity: 0.5;
  mask-image: radial-gradient(circle at 50% 40%, black, transparent 75%);
  -webkit-mask-image: radial-gradient(circle at 50% 40%, black, transparent 75%);
`;

const Row = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
  display: grid;
  grid-template-columns: 1fr;
  align-items: center;
  gap: 2.5rem;
  @media (min-width: 981px) {
    grid-template-columns: 1.05fr 0.95fr;
    gap: 3rem;
  }
`;

const OrbitCol = styled(motion.div)`
  display: none;
  @media (min-width: 981px) { display: block; }
`;

const Inner = styled.div`
  position: relative;
  z-index: 2;
  max-width: 780px;
`;

const Badge = styled(motion.div)`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.45rem 1rem;
  border: 1px solid var(--border-strong);
  border-radius: 999px;
  background: var(--surface);
  backdrop-filter: blur(8px);
  font-size: 0.85rem;
  color: var(--text-muted);
  margin-bottom: 1.6rem;

  .dot {
    width: 8px; height: 8px; border-radius: 50%; background: #22c55e;
    animation: pulse 2s infinite;
  }
  @keyframes pulse {
    0%{ box-shadow:0 0 0 0 rgba(34,197,94,0.5);}
    70%{ box-shadow:0 0 0 8px rgba(34,197,94,0);}
    100%{ box-shadow:0 0 0 0 rgba(34,197,94,0);}
  }
`;

const Title = styled(motion.h1)`
  font-size: clamp(2.6rem, 6.2vw, 4.8rem);
  margin-bottom: 1.1rem;
  .greet { display: block; color: var(--text); }
`;

const Typed = styled(motion.div)`
  font-family: var(--font-heading);
  font-size: clamp(1.2rem, 3vw, 1.9rem);
  font-weight: 600;
  margin-bottom: 1.4rem;
  min-height: 2.4rem;
  .label { color: var(--text-faint); font-weight: 500; }
  .typed-cursor { color: var(--primary-light); }
`;

const Lead = styled(motion.p)`
  font-size: clamp(1rem, 1.9vw, 1.18rem);
  color: var(--text-muted);
  max-width: 600px;
  margin-bottom: 1.4rem;
  @media (max-width: 768px) { margin-left: auto; margin-right: auto; }
`;

const Meta = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-muted);
  font-size: 0.95rem;
  margin-bottom: 2rem;
  svg { color: var(--primary-light); }
  @media (max-width: 768px) { justify-content: center; }
`;

const Actions = styled(motion.div)`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: center;
  margin-bottom: 2.5rem;
  @media (max-width: 768px) { justify-content: center; }
`;

const Primary = styled(Link)`
  display: inline-flex; align-items: center; gap: 0.6rem;
  padding: 0.95rem 1.7rem; border-radius: 999px;
  background: var(--gradient-accent); color: #fff; font-weight: 600; cursor: pointer;
  box-shadow: 0 12px 30px -10px rgba(99,102,241,0.6);
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  svg { transition: transform 0.25s ease; }
  &:hover { box-shadow: 0 18px 42px -12px rgba(236,72,153,0.6); }
  &:hover svg { transform: translateX(4px); }
`;

const Ghost = styled.a`
  display: inline-flex; align-items: center; gap: 0.6rem;
  padding: 0.95rem 1.6rem; border-radius: 999px;
  border: 1px solid var(--border-strong); background: var(--surface);
  color: var(--text); font-weight: 600; cursor: pointer;
  transition: background 0.25s ease, border-color 0.25s ease;
  &:hover { background: var(--surface-hover); border-color: var(--primary-light); }
`;

const Socials = styled.div`
  display: flex; gap: 0.5rem;
  a {
    display: grid; place-items: center; width: 46px; height: 46px; border-radius: 50%;
    border: 1px solid var(--border); color: var(--text-muted); font-size: 1.15rem;
    transition: all 0.25s ease;
  }
  a:hover { color: var(--text); border-color: var(--primary-light); transform: translateY(-3px); }
`;

const TechRow = styled(motion.div)`
  display: flex; align-items: center; flex-wrap: wrap; gap: 0.65rem;
  color: var(--text-faint); font-size: 0.85rem;
  .lbl { letter-spacing: 0.12em; text-transform: uppercase; margin-right: 0.25rem; }
  .chip {
    display: inline-flex; align-items: center; gap: 0.4rem;
    padding: 0.35rem 0.7rem; border-radius: 999px;
    border: 1px solid var(--border); background: var(--surface);
    color: var(--text); font-weight: 500;
  }
  .chip svg { color: var(--primary-light); font-size: 1rem; }
  @media (max-width: 768px) { justify-content: center; }
`;

const ScrollHint = styled(Link)`
  position: absolute; bottom: 1.6rem; left: 50%; transform: translateX(-50%); z-index: 2;
  display: flex; flex-direction: column; align-items: center; gap: 0.35rem;
  color: var(--text-faint); font-size: 0.72rem; letter-spacing: 0.15em; text-transform: uppercase; cursor: pointer;
  svg { font-size: 1.1rem; animation: ${bob} 1.8s ease-in-out infinite; }
  &:hover { color: var(--text-muted); }
  @media (max-width: 768px) { display: none; }
`;

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 22 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, delay, ease: 'easeOut' },
});

const heroTech = ['React', 'Java', 'Spring Boot', 'PHP', 'Flutter', 'Docker'];

const Hero = () => {
  const { t, data, lang } = useI18n();
  const { profile } = data;
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const meshX = useSpring(useTransform(mx, [-0.5, 0.5], [-30, 30]), { stiffness: 55, damping: 18 });
  const meshY = useSpring(useTransform(my, [-0.5, 0.5], [-22, 22]), { stiffness: 55, damping: 18 });
  const gridX = useSpring(useTransform(mx, [-0.5, 0.5], [14, -14]), { stiffness: 55, damping: 18 });
  const gridY = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), { stiffness: 55, damping: 18 });

  const onMouseMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
  <Wrap id="hero" onMouseMove={onMouseMove}>
    <ParallaxLayer style={{ x: meshX, y: meshY }}>
      <Mesh><span /><span /><span /></Mesh>
    </ParallaxLayer>
    <ParallaxLayer style={{ x: gridX, y: gridY }}>
      <Grid />
    </ParallaxLayer>

    <Container>
    <Row>
    <Inner>
      <Badge {...fade(0)}>
        <span className="dot" /> {profile.availability}
      </Badge>

      <Title>
        <span className="greet">
          <SplitText key={`greet-${lang}`} text={t.hero.greet} delay={0.15} />
        </span>
        <SplitText key={`role-${lang}`} text={profile.role} gradient delay={0.45} stagger={0.035} />
      </Title>

      <Typed {...fade(0.2)}>
        <span className="label">{t.hero.shipping}</span>
        <ReactTyped key={lang} strings={profile.typed} typeSpeed={55} backSpeed={35} backDelay={1600} loop />
      </Typed>

      <Lead {...fade(0.3)}>{profile.lead}</Lead>

      <Meta {...fade(0.36)}>
        <FaMapMarkerAlt /> {profile.location}
      </Meta>

      <Actions {...fade(0.42)}>
        <Magnetic>
          <Primary to="projects" smooth duration={500} offset={-70}>
            {t.hero.viewWork} <FaArrowRight />
          </Primary>
        </Magnetic>
        <Magnetic>
          <Ghost href={`${process.env.PUBLIC_URL}/${profile.cv}`} download>
            <FiDownload /> {t.hero.downloadCv}
          </Ghost>
        </Magnetic>
        <Socials>
          <a href={profile.github} target="_blank" rel="noopener noreferrer" aria-label="GitHub"><FaGithub /></a>
          <a href={profile.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn"><FaLinkedin /></a>
        </Socials>
      </Actions>

      <TechRow {...fade(0.5)}>
        <span className="lbl">{t.hero.stack}</span>
        {heroTech.map((t) => (
          <span className="chip" key={t}>{getTechIcon(t)}{t}</span>
        ))}
      </TechRow>
    </Inner>

      <OrbitCol
        initial={{ opacity: 0, scale: 0.85 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.9, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
      >
        <TechOrbit />
      </OrbitCol>
    </Row>
    </Container>

    <ScrollHint to="impact" smooth duration={500} offset={-70}>
      {t.hero.scroll} <HiArrowDown />
    </ScrollHint>
  </Wrap>
  );
};

export default Hero;
